import axios from "axios";
import {unwrapBillingCount, unwrapBillingData, unwrapBillingList} from "../helpers/billing-response";

class BillingService {
    getOrganizationBillingInfo() {
        return axios.get(`/billing/organization/get`)
    }

    getBillingPackages() {
        return axios.get('/billing/package/get')
    }

    activatePackage({ packageId }) {
        return axios.post('/billing/package/buy', {
            package_id: packageId,
        })
    }

    activateDemoPeriod({ serviceCode }) {
        return axios.post('/billing/demo/activate', {
            service_code: serviceCode,
        })
    }

    getEImzoSigners() {
        return axios.get('/roaming/admin/eimzo-signers/get')
    }

    registerEImzoSigner(params) {
        return axios.post(`/roaming/admin/eimzo-signers/register`, params)
    }

    getSystemOrganizations(params) {
        return axios.get('/auth/admin/organization/get', {
            params: params
        })
    }

    getSystemOrganizationsCount(params) {
        return axios.get('/auth/admin/organization/count', {
            params: params,
        })
    }

    addOrganizationBalance({ id, balance }) {
        return axios.post(`/billing/admin/payment/add-balance/${id}`, {
            balance: balance,
        })
    }

    applyCreditIntoOrganization({ id, amount }) {
        return axios.post(`/billing/admin/organization/update-credit-amount/${id}`, {
            credit_amount: +amount,
        })
    }

    getSystemOrganization(id) {
        return axios.get(`/auth/admin/organization/${id}`)
    }

    getPayments(params) {
        return axios.get('/billing/admin/payment', {
            params
        });
    }

    getPaymentsCount(params) {
        return axios.get('/billing/admin/payment/count', {
            params: params,
        })
    }

    getBankPaymentsError(params) {
        return axios.get('/billing/admin/bank-auto-payment-error', {
            params
        });
    }

    getBankPaymentsErrorCount(params) {
        return axios.get('/billing/admin/bank-auto-payment-error/count', {
            params: params,
        })
    }
    resolveBankPaymentsError(id) {
        return axios.post(`/billing/admin/bank-auto-payment-error/resolve/${id}`)
    }


    getOwnPayments(params) {
        return axios.get('/billing/organization/payment', {
            params
        })
    }
    getOwnPaymentsCount() {
        return axios.get('/billing/organization/payment/count')
    }

    getBought(params) {
        return axios.get('/billing/organization/bought-packages', {
            params
        })
    }

    getBoughtCount() {
        return axios.get('/billing/organization/bought-packages/count')
    }

    getCharges(params) {
        return axios.get('/billing/organization/charge', {
            params
        })
    }

    getChargesCount(params) {
        return axios.get('/billing/organization/charge/count', {
            params
        })
    }

    getOrganizationBalanceBindings(params) {
        return axios.get('/billing/admin/organization-balance-bindings', {
            params
        })
    }

    getOrganizationBalanceBindingsCount(params) {
        return axios.get('/billing/admin/organization-balance-bindings/count', {
            params
        })
    }

    addOrganizationBalanceBinding(payload) {
        return axios.post('/billing/admin/organization-balance-binding/add', payload)
    }

    removeOrganizationBalanceBinding(payload) {
        return axios.post(`/billing/admin/organization-balance-binding/remove`, payload)
    }

    // admin
    getChargesAdmin(params) {
        return axios.get('/billing/admin/organization/charge', {
            params
        })
    }

    getChargesAdminCount(params) {
        return axios.get('/billing/admin/organization/charge/count', {
            params
        })
    }

    getBoughtAdmin(params) {
        return axios.get('/billing/admin/organization/bought-packages', {
            params
        })
    }

    getBoughtAdminCount(params) {
        return axios.get('/billing/admin/organization/bought-packages/count', {
            params
        })
    }


    setAutoExtendPackage(params) {
        return axios.post('/billing/active-package/set-auto-extend', {
            active_package_id: params.active_package_id,
            is_auto_extend: params.is_auto_extend
        })
    }

    // credentials
    getCredentials(params) {
        return axios.get('/auth/admin/client-credentials/get', { params })
    }

    getCredentialsCount(params) {
        return axios.get('/auth/admin/client-credentials/count', { params })
    }

    registerCredential(payload) {
        return axios.post('/auth/admin/client-credentials/register', payload)
    }

    removeServiceDemoUses(payload) {
        return axios.post('/billing/admin/organization/remove-service-demo-use', payload)
    }

    // report
    getReport(params) {
        return axios.get('/billing/admin/report/services-usage-period-report', { params })
    }

    // =======================================================================
    // ERP Billing Service (yangi) — https://staging-savdo.hippo.uz/billing/swagger/index.html
    //
    // Yuqoridagi eski metodlardan farqi: bu metodlar axios javobini emas, allaqachon
    // envelope'dan yechilgan foydali yukni qaytaradi (gateway javoblarni {data: ...}
    // ichiga o'raydi). Ya'ni chaqiruvchi `const rows = await ...` deb yozadi,
    // `const {data} = await ...` emas.
    // =======================================================================

    // --- Paketlar katalogi (Package) ---

    /** Barcha paketlar: contracts.PackageContract[] */
    async getPackages() {
        const response = await axios.get('/billing/package/get-all');
        return unwrapBillingList(response);
    }

    // --- Admin: tashkilotning paket chegirmalari (AdminOrganizationPackageDiscount) ---

    /** Chegirmalar ro'yxati. params: {organization_id, skip, limit} */
    async getOrganizationPackageDiscounts(params) {
        const response = await axios.get('/billing/admin/organization-package-discount/get-all', {params});
        return unwrapBillingList(response);
    }

    /** Chegirmalar soni. params: {organization_id} */
    async getOrganizationPackageDiscountsCount(params) {
        const response = await axios.get('/billing/admin/organization-package-discount/get-all/count', {params});
        return unwrapBillingCount(response);
    }

    /** Bitta chegirma. */
    async getOrganizationPackageDiscount(id) {
        const response = await axios.get(`/billing/admin/organization-package-discount/get/${id}`);
        return unwrapBillingData(response);
    }

    /** Chegirma yaratish. payload: {organization_id, package_code, amount} */
    async createOrganizationPackageDiscount(payload) {
        const response = await axios.post('/billing/admin/organization-package-discount/create', payload);
        return unwrapBillingData(response);
    }

    /** Chegirma summasini o'zgartirish. payload: {amount} */
    async updateOrganizationPackageDiscount(id, payload) {
        const response = await axios.post(`/billing/admin/organization-package-discount/update/${id}`, payload);
        return unwrapBillingData(response);
    }

    /** Chegirmani o'chirish (soft-delete). */
    async deleteOrganizationPackageDiscount(id) {
        const response = await axios.post(`/billing/admin/organization-package-discount/delete/${id}`);
        return unwrapBillingData(response);
    }

    // --- Admin: paketlarning tashkilotga ko'rinishi (AdminOrganizationPackageVisibility) ---

    /**
     * Tashkilot uchun butun katalog, hisoblangan is_hidden va has_override bilan.
     * params: {organization_id} — majburiy.
     */
    async getOrganizationPackagesWithVisibility(params) {
        const response = await axios.get('/billing/admin/organization-package-visibility/organization-packages', {params});
        return unwrapBillingList(response);
    }

    /**
     * Ko'rinish istisnosini yaratish yoki qayta yozish (upsert).
     * payload: {organization_id, package_code, is_hidden}
     * is_hidden backend'da *bool — har doim aniq true/false yuborilishi shart.
     */
    async setOrganizationPackageVisibility(payload) {
        const response = await axios.post('/billing/admin/organization-package-visibility/set', payload);
        return unwrapBillingData(response);
    }

    /**
     * Bitta saqlangan istisno. params: {organization_id, package_code} — ikkalasi majburiy.
     * 404 — istisno yo'q, paket defaulti amal qiladi (xato emas): null qaytaramiz.
     */
    async getOrganizationPackageVisibility(params) {
        try {
            const response = await axios.get('/billing/admin/organization-package-visibility/get', {params});
            return unwrapBillingData(response);
        } catch (error) {
            if (error?.response?.status === 404) return null;
            throw error;
        }
    }

    /** Saqlangan istisnolar ro'yxati. params: {organization_id, package_code, is_hidden, skip, limit} */
    async getOrganizationPackageVisibilities(params) {
        const response = await axios.get('/billing/admin/organization-package-visibility/get-all', {params});
        return unwrapBillingList(response);
    }

    /** Saqlangan istisnolar soni. params: {organization_id, package_code, is_hidden} */
    async getOrganizationPackageVisibilitiesCount(params) {
        const response = await axios.get('/billing/admin/organization-package-visibility/get-all/count', {params});
        return unwrapBillingCount(response);
    }
}

const billingService = new BillingService();

export default billingService;
